import { CheckCircle2, ChevronRight, ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { usePsvReviewCount } from "../psv-review/review-query";

function plural({
  count,
  one,
  many,
}: {
  count: number;
  one: string;
  many: string;
}) {
  return count === 1 ? one : many;
}

/** How many providers wait on a reviewer, with a shortcut to them. */
export function ReviewBanner({
  organizationBusinessUnitId,
  onViewProviders,
}: {
  organizationBusinessUnitId: string;
  onViewProviders: () => void;
}) {
  const query = usePsvReviewCount(organizationBusinessUnitId);
  if (query.isPending) {
    return <Skeleton className="h-16 rounded-lg" aria-hidden="true" />;
  }
  if (query.isError) {
    return (
      <div
        role="status"
        className="flex flex-wrap items-center justify-between gap-2 rounded-lg border p-4 text-sm"
      >
        <p className="font-medium">Review count unavailable</p>
        <Button
          variant="outline"
          size="sm"
          disabled={query.isFetching}
          onClick={() => void query.refetch()}
        >
          Try loading the count again
        </Button>
      </div>
    );
  }
  const { totalProviders, totalItems } = query.data;
  if (totalProviders === 0) {
    return (
      <p
        role="status"
        className="flex items-center gap-2 text-sm text-muted-foreground"
      >
        <CheckCircle2 className="size-4 text-emerald-600" aria-hidden="true" />
        All caught up. Completed verifications stay available on each provider.
      </p>
    );
  }
  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        "flex flex-wrap items-center justify-between gap-3 rounded-lg border p-4",
        "border-amber-200 bg-amber-50/70",
      )}
    >
      <div className="flex min-w-0 items-start gap-3">
        <ShieldAlert
          className="mt-0.5 size-5 shrink-0 text-amber-700"
          aria-hidden="true"
        />
        <div>
          <p className="font-semibold">
            <span className="tabular-nums">{totalProviders}</span>{" "}
            {plural({
              count: totalProviders,
              one: "provider needs",
              many: "providers need",
            })}{" "}
            review
          </p>
          <p className="text-sm text-muted-foreground">
            <span className="tabular-nums">{totalItems}</span>{" "}
            {plural({ count: totalItems, one: "item is", many: "items are" })}{" "}
            waiting for Accept or Concern
          </p>
        </div>
      </div>
      <Button variant="ghost" className="gap-1" onClick={onViewProviders}>
        View providers
        <ChevronRight className="size-4" aria-hidden="true" />
      </Button>
    </div>
  );
}
